import { useEffect, useState } from "react";
import type { Locale } from "@project/i18n";
import { SUPPORTED_LOCALES } from "@project/i18n";
import { Button } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { AccountDeleteConfirmDialog } from "@apps/dashboard/features/account/account-delete-confirm-dialog";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";

export default function SettingsPersonalDataPage(): JSX.Element {
  const { t, locale, setLocale } = useI18n();
  const { data, refetch } = authClient.useSession();
  const [nameEdit, setNameEdit] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const user = data?.user;
  const email = typeof user?.email === "string" ? user.email : "";

  useEffect(() => {
    setNameEdit(typeof user?.name === "string" ? user.name : "");
  }, [user]);

  async function handleProfileSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setProfileError(null);
    setProfileSaved(false);
    setProfileSaving(true);
    try {
      const trimmedName = nameEdit.trim();
      const { error: err } = await authClient.updateUser({
        name: trimmedName.length > 0 ? trimmedName : "",
      });
      if (err) {
        setProfileError(translateAuthError(err, t));
        return;
      }
      await refetch();
      setProfileSaved(true);
    } catch (unknownErr) {
      setProfileError(translateAuthError(unknownErr, t));
    } finally {
      setProfileSaving(false);
    }
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.settings}>{t("dashboardUi.settingsHub.backToAccount")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.pages.settingsPersonal")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">
        {t("dashboardUi.personalDataPage.subtitle")}
      </p>

      <div className="mt-8 rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-8 shadow-sm">
        <div>
          <p className="font-label text-label-md text-on-surface">{t("dashboardUi.personalDataPage.emailLabel")}</p>
          <p className="mt-2 rounded-xl border border-outline-variant/25 bg-surface px-4 py-3 font-body text-body-md text-on-surface">
            {email.length > 0 ? email : "—"}
          </p>
          <p className="mt-2 font-body text-xs text-on-surface-variant">{t("dashboardUi.personalDataPage.emailHint")}</p>
        </div>

        <div className="mt-8 border-t border-outline-variant/10 pt-8">
          <h2 className="font-headline text-lg font-bold tracking-tight text-on-surface">
            {t("dashboardUi.profile.profileFormSectionTitle")}
          </h2>
          <label htmlFor="personal-locale" className="mt-4 block font-label text-label-md text-on-surface">
            {t("common.locale.label")}
          </label>
          <select
            id="personal-locale"
            value={locale}
            onChange={(e) => setLocale(e.target.value as Locale)}
            className="mt-2 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
          >
            {SUPPORTED_LOCALES.map((loc) => (
              <option key={loc} value={loc}>
                {t(`common.locale.${loc}`)}
              </option>
            ))}
          </select>
          <p className="mt-2 font-body text-xs text-on-surface-variant">{t("common.locale.hint")}</p>

          <form onSubmit={(e) => void handleProfileSave(e)} className="mt-6 border-t border-outline-variant/10 pt-6">
            {profileError ? (
              <div className="mb-4 rounded-lg bg-error/10 px-3 py-2 text-sm text-error" role="alert">
                {profileError}
              </div>
            ) : null}
            {profileSaved ? (
              <p className="mb-4 text-sm text-primary" role="status">
                {t("dashboardUi.profile.savedHint")}
              </p>
            ) : null}
            <div className="mb-4">
              <label htmlFor="personal-name" className="mb-1 block font-label text-label-md text-on-surface">
                {t("dashboardUi.profile.nameField")}
              </label>
              <input
                id="personal-name"
                type="text"
                autoComplete="name"
                value={nameEdit}
                onChange={(e) => {
                  setNameEdit(e.target.value);
                  setProfileSaved(false);
                }}
                className="w-full rounded-xl border border-outline-variant/25 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none ring-primary focus:ring-2"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={profileSaving}
                className="rounded-xl bg-primary px-6 py-3 font-headline text-sm font-bold text-on-primary shadow-md disabled:opacity-50"
              >
                {profileSaving ? t("dashboardUi.profile.saving") : t("dashboardUi.profile.saveButton")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-error/25 bg-error/5 p-8 shadow-sm">
        <h2 className="font-headline text-lg font-bold tracking-tight text-error">
          {t("dashboardUi.personalDataPage.deleteAccountSectionTitle")}
        </h2>
        <p className="mt-2 font-body text-body-md text-on-surface-variant">
          {t("dashboardUi.personalDataPage.deleteAccountSectionHint")}
        </p>
        <div className="mt-6">
          <Button
            type="button"
            variant="destructive"
            className="rounded-xl"
            onClick={() => setDeleteModalOpen(true)}
          >
            {t("dashboardUi.personalDataPage.deleteAccountButton")}
          </Button>
        </div>
      </div>

      <AccountDeleteConfirmDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen} />
    </div>
  );
}
